import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  categoryService,
  userService,
  productService,
  adminService,
  activityService,
  notificationService,
  reportService,
  videoService
} from '../services/api';
import {
  Category,
  UserProgress,
  User,
  DashboardStats,
  UserActivity,
  SubcategoryHierarchy,
  Certificate,
  Notification,
  Video,
  CreateVideoData,
  UpdateVideoData,
  UserGroup
} from '../types';
import { exportDataToCSV, exportDataToExcel, exportUnifiedReport } from '../utils/downloadUtils';
import toast from 'react-hot-toast';

/** ----------------------------------------------------------------
 * Util para estabilizar llaves de React Query cuando la key incluye
 * objetos/params. Evita que se creen keys nuevas por referencias nuevas.
 * ---------------------------------------------------------------- */
const stableKey = (obj?: unknown) => (obj ? JSON.stringify(obj) : '');

/** ================================================================
 * Hook para manejar el estado de carga
 * ================================================================ */
export const useLoading = (initialState = false) => {
  const [isLoading, setIsLoading] = useState(initialState);
  return { isLoading, setIsLoading };
};

/** ================================================================
 * Hook para manejar modales
 * ================================================================ */
export const useModal = (initialState = false) => {
  const [isOpen, setIsOpen] = useState(initialState);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);
  const toggleModal = useCallback(() => setIsOpen(prev => !prev), []);

  return { isOpen, openModal, closeModal, toggleModal };
};

/** ================================================================
 * Hook para detectar dispositivos móviles
 * ================================================================ */
export const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

/** ================================================================
 * Hook para obtener categorías
 * ================================================================ */
export const useCategories = () => {
  return useQuery({
    queryKey: ['categories'],
    queryFn: categoryService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000,   // 10 minutos
  });
};

/** ================================================================
 * Hook para obtener categoría específica
 * ================================================================ */
export const useCategory = (id: string) => {
  return useQuery({
    queryKey: ['category', id],
    queryFn: () => categoryService.getById(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });
};

/** ================================================================
 * Hook para obtener progreso del usuario
 * ================================================================ */
export const useUserProgress = (categoryId: string) => {
  return useQuery({
    queryKey: ['userProgress', categoryId],
    queryFn: () => userService.getProgress(categoryId),
    enabled: !!categoryId,
    staleTime: 1 * 60 * 1000, // 1 minuto
  });
};

/** ================================================================
 * Hook subcategoria hijos (cálculo de progreso jerárquico en memoria)
 * ================================================================ */
export const useHierarchicalProgress = (
  subcategories: SubcategoryHierarchy[],
  activities: UserActivity[]
) => {
  return useMemo(() => {
    // Contar todos los productos recursivamente
    const countTotalProducts = (subs: SubcategoryHierarchy[]): number => {
      return subs.reduce((total, sub) => {
        let count = sub.products?.length || 0;
        if (sub.children && sub.children.length > 0) {
          count += countTotalProducts(sub.children);
        }
        return total + count;
      }, 0);
    };

    // Extraer productos vistos de las actividades
    const getViewedProducts = (): Set<string> => {
      const viewedProducts = new Set<string>();

      activities.forEach(activity => {
        if (activity.activityType === 'portfolio' && activity.metadata?.viewedProducts) {
          activity.metadata.viewedProducts.forEach((productId: string) => {
            viewedProducts.add(productId);
          });
        }
      });

      return viewedProducts;
    };

    const totalProducts = countTotalProducts(subcategories);
    const viewedProducts = getViewedProducts();
    const completedActivities = activities.filter(a => a.completed).length;

    // Calcular progreso por tipo de actividad
    const videoProgress = activities.some(a => a.activityType === 'video' && a.completed);
    const arProgress = activities.some(a => a.activityType === 'ar' && a.completed);
    const portfolioProgress = activities.some(a => a.activityType === 'portfolio' && a.completed);
    const testProgress = activities.some(a => a.activityType === 'test' && a.completed);

    const totalActivities = 4; // video, ar, portfolio, test
    const overallProgress = totalActivities > 0 ? (completedActivities / totalActivities) * 100 : 0;

    return {
      video: videoProgress,
      ar: arProgress,
      portfolio: portfolioProgress,
      test: testProgress,
      totalTimeSpent: activities.reduce((total, a) => total + (a.timeSpent || 0), 0),
      completedActivities,
      totalActivities,
      overallProgress,
      portfolioDetails: {
        totalProducts,
        viewedProducts: viewedProducts.size,
        portfolioProgress: totalProducts > 0 ? (viewedProducts.size / totalProducts) * 100 : 0
      }
    };
  }, [subcategories, activities]);
};

/** ================================================================
 * Hook para registrar actividad
 * ================================================================ */
export const useRecordActivity = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: activityService.recordActivity,
    onSuccess: (data, variables) => {
      // Invalidar cache del progreso del usuario
      queryClient.invalidateQueries({ queryKey: ['userProgress', variables.categoryId] });
      queryClient.invalidateQueries({ queryKey: ['userActivities'] });
      queryClient.invalidateQueries({ queryKey: ['activitySummary'] }); // ✅ nuevo

      if (variables.completed) {
        toast.success('¡Actividad completada!');
      }
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error registrando actividad');
    },
  });
};

export const useActivitySummary = () => {
  return useQuery({
    queryKey: ['activitySummary'],
    queryFn: async () => {
      const res = await activityService.getSummary();
      return res.data; // devolvemos solo el bloque con { video, ar, test, portfolio, totalCompleted }
    },
    staleTime: 1000 * 60 * 5, // cachea por 5 minutos
  });
};

/** ================================================================
 * Hook para perfil de usuario
 * ================================================================ */
export const useUserProfile = () => {
  return useQuery({
    queryKey: ['userProfile'],
    queryFn: userService.getProfile,
    staleTime: 10 * 60 * 1000,
  });
};

/** ================================================================
 * Hook para actividades del usuario (con params estables)
 * ================================================================ */
export const useUserActivities = (params?: {
  page?: number;
  limit?: number;
  categoryId?: string;
  activityType?: string;
}) => {
  const key = ['userActivities', stableKey(params)];
  return useQuery({
    queryKey: key,
    queryFn: () => userService.getActivities(params),
    staleTime: 2 * 60 * 1000,
    // En listados/paginación queremos evitar flicker al cambiar params
    placeholderData: keepPreviousData,
  });
};

/** ================================================================
 * Hook para certificados del usuario
 * ================================================================ */
export const useUserCertificates = () => {
  return useQuery({
    queryKey: ['userCertificates'],
    queryFn: userService.getCertificates,
    staleTime: 5 * 60 * 1000,
  });
};

/** ================================================================
 * Hook para dashboard de admin
 * ================================================================ */
export const useAdminDashboard = () => {
  return useQuery({
    queryKey: ['adminDashboard'],
    queryFn: adminService.getDashboard,
    staleTime: 2 * 60 * 1000,
  });
};

/** ================================================================
 * Hook para usuarios (admin) con params estables
 * ================================================================ */
export const useAdminUsers = (params?: {
  page?: number;
  limit?: number;
  search?: string;
  status?: string;
  zone?: string;
}) => {
  const key = ['adminUsers', stableKey(params)];
  return useQuery({
    queryKey: key,
    queryFn: () => adminService.getUsers(params),
    staleTime: 3 * 60 * 1000,
    // Evita parpadeo al paginar/filtrar
    placeholderData: keepPreviousData,
  });
};

/** ================================================================
 * Hook para crear usuario (admin)
 * ================================================================ */
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminService.createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminDashboard'] });
      toast.success('Usuario creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error creando usuario');
    },
  });
};

/** ================================================================
 * Hook para carga masiva de usuarios
 * ================================================================ */
export const useBulkUploadUsers = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminService.bulkUploadUsers,
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminDashboard'] });
      toast.success(data.message || 'Usuarios cargados exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error en carga masiva');
    },
  });
};

/** ================================================================
 * Hook para actualizar usuario (admin)
 * ================================================================ */
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: Partial<User> & { password?: string } }) =>
      adminService.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminDashboard'] });
      toast.success('Usuario actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error actualizando usuario');
    },
  });
};

/** ================================================================
 * Hook para eliminar usuario (admin)
 * ================================================================ */
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => adminService.deleteUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminUsers'] });
      queryClient.invalidateQueries({ queryKey: ['adminDashboard'] });
      toast.success('Usuario eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error eliminando usuario');
    },
  });
};

/** ================================================================
 * Hook para notificaciones del usuario (params estables)
 * ================================================================ */
export const useUserNotifications = (params?: {
  page?: number;
  limit?: number;
}) => {
  const key = ['userNotifications', stableKey(params)];
  return useQuery({
    queryKey: key,
    queryFn: () => notificationService.getUserNotifications(params),
    staleTime: 1 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** ================================================================
 * Hook para crear notificación (admin)
 * ================================================================ */
export const useCreateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('Notificación enviada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error enviando notificación');
    },
  });
};

/** ================================================================
 * Hook para productos por subcategoría
 * ================================================================ */
export const useProductsBySubcategory = (subcategoryId: string) => {
  return useQuery({
    queryKey: ['products', subcategoryId],
    queryFn: () => productService.getBySubcategory(subcategoryId),
    enabled: !!subcategoryId,
    staleTime: 5 * 60 * 1000,
  });
};

/** ================================================================
 * Hook para temporizador de actividad
 * ================================================================ */
export const useActivityTimer = () => {
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const getElapsedSeconds = useCallback(() => {
    return Math.floor((Date.now() - startTime) / 1000);
  }, [startTime]);

  return { elapsedTime, getElapsedSeconds };
};

/** ================================================================
 * Hook para detección de soporte AR
 * ================================================================ */
export const useARSupport = () => {
  const [isARSupported, setIsARSupported] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    const checkARSupport = async () => {
      try {
        // Verificar soporte de WebXR
        if ('xr' in navigator) {
          const xr = (navigator as any).xr;
          if (xr) {
            const isSupported = await xr.isSessionSupported('immersive-ar');
            setIsARSupported(isSupported);
          }
        } else {
          // Fallback: detectar dispositivo móvil para 8thwall
          const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
            navigator.userAgent
          );
          setIsARSupported(isMobile);
        }
      } catch (error) {
        console.error('Error checking AR support:', error);
        setIsARSupported(false);
      } finally {
        setIsChecking(false);
      }
    };

    checkARSupport();
  }, []);

  return { isARSupported, isChecking };
};

/** ================================================================
 * Hook para persistir estado en localStorage
 * ================================================================ */
export const useLocalStorage = <T>(key: string, initialValue: T) => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(`Error reading localStorage key "${key}":`, error);
      return initialValue;
    }
  });

  const setValue = useCallback((value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error(`Error setting localStorage key "${key}":`, error);
    }
  }, [key, storedValue]);

  const removeValue = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
      setStoredValue(initialValue);
    } catch (error) {
      console.error(`Error removing localStorage key "${key}":`, error);
    }
  }, [key, initialValue]);

  return [storedValue, setValue, removeValue] as const;
};

/** ================================================================
 * Hook para marcar notificación como leída
 * ================================================================ */
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
      toast.success('Notificación marcada como leída');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error marcando como leída');
    },
  });
};

/** ================================================================
 * Hook para eliminar notificación
 * ================================================================ */
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: notificationService.deleteUserNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotifications'] });
      toast.success('Notificación eliminada');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error eliminando notificación');
    },
  });
};

/** ================================================================
 * Hook para obtener todas las notificaciones (admin)
 * ================================================================ */
export const useAdminNotifications = (params?: {
  page?: number;
  limit?: number;
}) => {
  const key = ['adminNotifications', stableKey(params)];
  return useQuery({
    queryKey: key,
    queryFn: () => notificationService.getAll(params),
    staleTime: 1 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** ================================================================
 * Hook para obtener notificación específica (admin)
 * ================================================================ */
export const useNotification = (id: string) => {
  return useQuery({
    queryKey: ['notification', id],
    queryFn: () => notificationService.getById(id),
    enabled: !!id,
    staleTime: 2 * 60 * 1000,
  });
};

/** ================================================================
 * Hook para actualizar notificación (admin)
 * ================================================================ */
export const useUpdateNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<any> }) =>
      notificationService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
      queryClient.invalidateQueries({ queryKey: ['notification'] });
      queryClient.invalidateQueries({ queryKey: ['notificationStats'] });
      toast.success('Notificación actualizada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error actualizando notificación');
    },
  });
};

/** ================================================================
 * Hook para eliminar notificación (admin)
 * ================================================================ */
export const useDeleteAdminNotification = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminNotifications'] });
      queryClient.invalidateQueries({ queryKey: ['notificationStats'] });
      toast.success('Notificación eliminada exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error eliminando notificación');
    },
  });
};

/** ================================================================
 * Hook para estadísticas de notificaciones (admin)
 * ================================================================ */
export const useNotificationStats = () => {
  return useQuery({
    queryKey: ['notificationStats'],
    queryFn: notificationService.getStats,
    staleTime: 1 * 60 * 1000, // Refrescar cada minuto
    refetchInterval: 60 * 1000, // Auto-refrescar cada minuto
  });
};

/** ================================================================
 * Hook para reportes de admin (dateRange estable)
 * ================================================================ */
export const useAdminReports = (dateRange?: string) => {
  const key = ['adminReports', stableKey(dateRange)];
  return useQuery({
    queryKey: key,
    queryFn: () => reportService.getDashboard(dateRange),
    staleTime: 2 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** ================================================================
 * Hook para exportar datos con descarga automática (CSV o Excel)
 * ================================================================ */
export const useExportData = () => {
  return useMutation({
    mutationFn: async ({
      type,
      dateRange,
      format = 'csv' // NUEVO: permite elegir formato ('csv' o 'excel')
    }: {
      type: 'users' | 'activities' | 'certificates';
      dateRange?: string;
      format?: 'csv' | 'excel';
    }) => {
      // Llamar al backend para obtener los datos
      const response = await reportService.exportData(type, dateRange);

      // Extraer los datos del response
      const data = response.data || [];

      if (!data || data.length === 0) {
        throw new Error('No hay datos disponibles para exportar');
      }

      // Convertir y descargar según el formato elegido
      if (format === 'excel') {
        await exportDataToExcel(data, type, dateRange);
      } else {
        exportDataToCSV(data, type, dateRange, 'csv');
      }

      return response;
    },
    onSuccess: (data, variables) => {
      const typeNames = {
        users: 'usuarios',
        activities: 'actividades',
        certificates: 'certificados'
      };

      const formatLabel = variables.format === 'excel' ? 'Excel' : 'CSV';
      toast.success(`Reporte de ${typeNames[variables.type]} descargado exitosamente (${formatLabel})`);
    },
    onError: (error: any) => {
      console.error('Error exportando datos:', error);
      toast.error(error.message || error.response?.data?.error || 'Error exportando datos');
    },
  });
};

/** ================================================================
 * Hook para exportar todos los reportes de una vez
 * ================================================================ */
export const useExportAllReports = () => {
  return useMutation({
    mutationFn: async ({
      dateRange,
      format = 'csv' // NUEVO: permite elegir formato
    }: {
      dateRange?: string;
      format?: 'csv' | 'excel';
    }) => {
      const types: Array<'users' | 'activities' | 'certificates'> = ['users', 'activities', 'certificates'];
      const results = [];

      for (const type of types) {
        try {
          const response = await reportService.exportData(type, dateRange);
          const data = response.data || [];

          if (data && data.length > 0) {
            if (format === 'excel') {
              await exportDataToExcel(data, type, dateRange);
            } else {
              exportDataToCSV(data, type, dateRange, 'csv');
            }
            results.push({ type, success: true, count: data.length });
          } else {
            results.push({ type, success: false, error: 'Sin datos' });
          }

          // Pequeña pausa entre descargas para no saturar el navegador
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          results.push({ type, success: false, error });
        }
      }

      return results;
    },
    onSuccess: (results, variables) => {
      const successCount = results.filter(r => r.success).length;
      const totalCount = results.length;
      const formatLabel = variables.format === 'excel' ? 'Excel' : 'CSV';

      if (successCount === totalCount) {
        toast.success(`Todos los reportes (${successCount}) descargados exitosamente en formato ${formatLabel}`);
      } else if (successCount > 0) {
        toast.success(`${successCount} de ${totalCount} reportes descargados en formato ${formatLabel}`);
      } else {
        toast.error('No se pudo descargar ningún reporte');
      }
    },
    onError: (error: any) => {
      console.error('Error exportando reportes:', error);
      toast.error('Error exportando reportes');
    },
  });
};

/** ================================================================
 * Hook para exportar reporte unificado
 * ================================================================ */
export const useExportUnifiedReport = () => {
  return useMutation({
    mutationFn: async ({ dateRange }: { dateRange?: string }) => {
      // Obtener los 3 datasets en paralelo
      const { users, activities, certificates } = await reportService.exportUnified(dateRange);

      if (!users.length) {
        throw new Error('No hay usuarios para exportar');
      }

      // Generar Excel unificado
      await exportUnifiedReport(users, activities, certificates, dateRange);

      return { users: users.length, activities: activities.length, certificates: certificates.length };
    },
    onSuccess: (counts) => {
      toast.success(
        `Reporte unificado descargado: ${counts.users} usuarios, ${counts.activities} actividades, ${counts.certificates} certificados`
      );
    },
    onError: (error: any) => {
      console.error('Error exportando reporte unificado:', error);
      toast.error(error.message || 'Error exportando reporte unificado');
    },
  });
};

/** ================================================================
 * Hooks para grupos (params estables)
 * ================================================================ */
export const useAdminGroups = (params?: { page?: number; limit?: number; search?: string }) => {
  const key = ['adminGroups', stableKey(params)];
  return useQuery({
    queryKey: key,
    queryFn: () => adminService.getGroups(params),
    staleTime: 1 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminService.createGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Grupo creado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error creando grupo');
    },
  });
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, data }: { groupId: string; data: Partial<UserGroup> }) =>
      adminService.updateGroup(groupId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Grupo actualizado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error actualizando grupo');
    },
  });
};

export const useDeleteGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => adminService.deleteGroup(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Grupo eliminado exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error eliminando grupo');
    },
  });
};

export const useAddUsersToGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: string[] }) =>
      adminService.addUsersToGroup(groupId, userIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Usuarios agregados al grupo exitosamente');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error agregando usuarios al grupo');
    },
  });
};

export const useRemoveUserFromGroup = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ groupId, userId }: { groupId: string; userId: string }) =>
      adminService.removeUserFromGroup(groupId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Usuario removido del grupo');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Error removiendo usuario');
    },
  });
};